import { useContext, useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Filter, X, Target, Users, ChevronDown, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import Pagination from '../../../../../../components/Pagination';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';
import auditProgramService from '../../../../../../services/audit_services/auditProgramService';

export default function AuditPlansList() {
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError } = useToast();

  const [auditPlans, setAuditPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    title: '',
    program: '',
    program_title: '', // Store program title for display
    status: '',
    ordering: 'newest'
  });

  // Program search state
  const [programSearch, setProgramSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [showFiltersSection, setShowFiltersSection] = useState(false);

  useEffect(() => {
    fetchAuditPlans();
  }, [pagination.page, pagination.page_size]);

  const fetchAuditPlans = async () => {
    setLoading(true);
    
    try {
      const result = await auditPlanService.getAuditPlans(
        authTokens.access, 
        filters, 
        { page: pagination.page, page_size: pagination.page_size }
      );
      
      if (result.success) {
        setAuditPlans(result.data.results);
        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        console.error('Error fetching audit plans:', result.error);
        showError(result.error || 'Error loading audit plans');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit plans:', error);
      showError('Error loading audit plans');
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    const clearedFilters = {
      title: '',
      program: '',
      program_title: '',
      status: '',
      ordering: 'newest'
    };
    setFilters(clearedFilters);
    setProgramSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Use the cleared filters directly in the API call
    const result = auditPlanService.getAuditPlans(
      authTokens.access, 
      clearedFilters, 
      { page: 1, page_size: pagination.page_size }
    );
    
    result.then((response) => {
      if (response.success) {
        setAuditPlans(response.data.results);
        setPagination(prev => ({
          ...prev,
          count: response.data.count,
          total_pages: response.data.total_pages,
          page: 1
        }));
      } else {
        console.error('Error fetching audit plans:', response.error);
        showError(response.error || 'Error loading audit plans');
      }
    }).catch((error) => {
      console.error('Error fetching audit plans:', error);
      showError('Error loading audit plans');
    });
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchAuditPlans();
  };

  // Program search functionality
  const handleProgramSearch = async (query) => {
    setProgramSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const result = await auditProgramService.searchAuditPrograms(authTokens.access, query);
        
        if (result.success) {
          setProgramSearch(prev => ({
            ...prev,
            results: result.data,
            showDropdown: true,
            loading: false
          }));
        } else {
          console.error('Error searching programs:', result.error);
          setProgramSearch(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error searching programs:', error);
        setProgramSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setProgramSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  const selectProgram = (program) => {
    setFilters(prev => ({
      ...prev,
      program: program.id,
      program_title: program.title
    }));
    
    // Clear search
    setProgramSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  // Click outside handler for program search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (programSearch.showDropdown && !event.target.closest('.program-search-container')) {
        setProgramSearch(prev => ({ ...prev, showDropdown: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [programSearch.showDropdown]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      released: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Released' },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'In Progress' },
      completed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Completed' },
      published: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Published' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Achieved' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredAuditPlans = auditPlans; // Server-side filtering, no need for client-side

  // Apply sorting
  const sortedAuditPlans = [...filteredAuditPlans];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des plans d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Gérer et suivre tous les plans d'audit</p>
        </div>
        {hasRole('AuditManager') && (
          <Link
            to="/dashboard/audit/plans/create"
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un plan d'audit
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par titre d'audit..."
              value={filters.title}
              onChange={(e) => handleFilterChange('title', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFiltersSection(!showFiltersSection)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={applyFilters}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Appliquer les filtres
          </button>
        </div>

        {/* Filters Section */}
        {showFiltersSection && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Program Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Programme</label>
                <div className="program-search-container relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={filters.program_title || programSearch.query}
                      onChange={(e) => {
                        if (!filters.program) {
                          handleProgramSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (!filters.program) {
                          handleProgramSearch(programSearch.query || '');
                        }
                      }}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Rechercher et sélectionner un programme..."
                      readOnly={!!filters.program}
                    />
                    {filters.program ? (
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(prev => ({ ...prev, program: '', program_title: '' }));
                          setProgramSearch({ query: '', results: [], showDropdown: false, loading: false });
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : (
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    )}
                  </div>

                  {/* Program Search Dropdown */}
                  {programSearch.showDropdown && !filters.program && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {programSearch.loading ? (
                        <div className="p-3 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                          <span className="ml-2">Recherche...</span>
                        </div>
                      ) : programSearch.results.length > 0 ? (
                        programSearch.results.map((program) => (
                          <button
                            key={program.id}
                            type="button"
                            onClick={() => selectProgram(program)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">{program.title}</p>
                              <p className="text-xs text-gray-500">
                                {program.objective ? program.objective.substring(0, 100) + '...' : 'Aucune description'}
                              </p>
                            </div>
                          </button>
                        ))
                      ) : programSearch.query.length >= 2 ? (
                        <div className="p-3 text-center text-gray-500">Aucun programme trouvé</div>
                      ) : (
                        <div className="p-3 text-center text-gray-500">Tapez pour rechercher des programmes...</div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Rechercher un programme et sélectionner dans la liste déroulante</p>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="released">Publié</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Complété</option>
                  <option value="published">Publié</option>
                  <option value="closed">Clôturé</option>
                  <option value="achieved">Archivé</option>
                </select>
              </div>

              {/* Sorting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
                <select
                  value={filters.ordering}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="newest">Plus récent d'abord</option>
                  <option value="oldest">Plus ancien d'abord</option>
                  <option value="title_asc">Titre (A-Z)</option>
                  <option value="title_desc">Titre (Z-A)</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center justify-end space-x-3 mt-4">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Effacer les filtres
              </button>
              <button
                onClick={applyFilters}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Appliquer les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Audit Plans Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {sortedAuditPlans.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun plan d'audit trouvé</p>
            {hasRole('AuditManager') && (
              <Link
                to="/dashboard/audit/plans/create"
                className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer votre premier plan d'audit
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails de l'audit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parties
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Risques
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedAuditPlans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{plan.title}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">Scope: {plan.scope}</p>
                        <p className="text-xs text-gray-500">Ref: {plan.reference}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {plan.location}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plan.program ? (
                        <Link
                          to={`/dashboard/audit/programs/${plan.program.id}`}
                          className="text-sm text-blue-600 hover:text-blue-900"
                        >
                          {plan.program.title}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">Aucun programme</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center mb-1">
                          <Users className="h-3 w-3 mr-1" />
                          <span className="font-medium">{plan.parties ? plan.parties.length : 0} parties impliquées</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {plan.parties && plan.parties.slice(0, 1).map((party, index) => (
                            <div key={index} className="flex items-center space-x-1 ">
                              <span>{party.name}</span>
                              <span className={`px-1 rounded text-xs ${
                                party.partyRole === 'auditor' ? 'bg-blue-100 text-blue-800' :
                                party.partyRole === 'auditee' ? 'bg-green-100 text-green-800' :
                                party.partyRole === 'participant' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {party.partyRole}
                              </span>
                            </div>
                          ))}
                          {plan.parties && plan.parties.length > 1 && (
                            <span className="text-gray-400">+{plan.parties.length - 1} more</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {plan.risks && plan.risks.length > 0 ? (
                          <>
                            <p className="font-medium">{plan.risks.length} risques identifiés</p>
                            <div className="text-xs text-gray-500 mt-1">
                              {plan.risks.slice(0, 1).map((risk, index) => (
                                <div key={index} className="flex items-center space-x-1">
                                  <span className='line-clamp-2'>{risk.name}</span>
                                  <span className={`px-1 rounded text-xs ${
                                    risk.probability === 'critical' ? 'bg-red-100 text-red-800' :
                                    risk.probability === 'high' ? 'bg-orange-100 text-orange-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {risk.probability}
                                  </span>
                                </div>
                              ))}
                              {plan.risks.length > 1 && (
                                <span className="text-gray-400">+{plan.risks.length - 1} more</span>
                              )}
                            </div>
                          </>
                        ) : (
                          <p className="text-gray-400">Aucun risque identifié</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getStatusBadge(plan.status)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        {/* View Details */}
                        <Link
                          to={`/dashboard/audit/plans/${plan.id}`}
                          className="text-green-600 hover:text-green-900"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                        
                        {/* Edit - Only for AuditManager when program is opened and plan is not closed/achieved */}
                        {hasRole('AuditManager') && 
                         plan.program?.status === 'opened' && 
                         plan.status !== 'closed' && 
                         plan.status !== 'achieved' && (
                          <Link
                            to={`/dashboard/audit/plans/${plan.id}/edit`}
                            className="text-green-600 hover:text-green-900"
                            title="Modifier"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                        )}
                        
                        {/* Disabled Edit - Show when conditions are not met */}
                        {hasRole('AuditManager') && 
                         (plan.program?.status !== 'opened' || 
                          plan.status === 'closed' || 
                          plan.status === 'achieved') && (
                          <span
                            className="text-gray-400 cursor-not-allowed"
                            title={
                              plan.program?.status !== 'opened' 
                                ? `Cannot edit when program status is ${plan.program?.status || 'unknown'}`
                                : `Cannot edit when audit plan is ${plan.status}`
                            }
                          >
                            <Edit className="h-5 w-5" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {sortedAuditPlans.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}
    </div>
  );
}